import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal } from "@/lib/motion";
import { EVENT, money, isEarlyBird, PACKAGE_LABELS } from "@/lib/site";
import { useBrochureGate } from "./Growth";

const RATES = {
  delegate: { early: [7500, 600], standard: [10000, 1100] },
  stall6: [80000, 2000],
  stall9: [125000, 2500],
  corporate: [15000, 1500],
  startup: [11000, 2000],
};

const TABS = [
  ["delegate", "Delegate passes"],
  ["stall", "Exhibition stalls"],
  ["awards", "Awards & gala"],
];

const PACKAGES_BY_TAB = {
  delegate: [
    { id: "delegate", name: "Early bird delegate", later: "Standard delegate", description: "Participation · Package 1. Connect with the organic food and beverage business ecosystem." },
    { id: "standard", name: "Standard / spot delegate", description: "Participation · Package 1. Rates applicable after 10 November 2026 and for spot registration.", disabledDuringEarly: true },
  ],
  stall: [
    { id: "stall6", name: "6 sq. m exhibition stall", description: "A platform to showcase your products, technology and services. Stall allocation subject to availability." },
    { id: "stall9", name: "9 sq. m exhibition stall", description: "Give your business room to connect with buyers and industry partners. Contact Tefla's to confirm availability." },
  ],
  awards: [
    { id: "corporate", name: "Corporates & entrepreneurs", description: "Award nomination entry fee and gala dinner only. 11 December 2026 · 7:00 pm onwards." },
    { id: "startup", name: "Startups & NGOs", description: "Award nomination entry fee and gala dinner only. 11 December 2026 · 7:00 pm onwards." },
  ],
};

export function Pricing() {
  const [tab, setTab] = useState("delegate");
  const [currency, setCurrency] = useState("INR");
  const early = isEarlyBird();
  const goToRegister = (pkgId) => {
    const actual = pkgId === "standard" ? "delegate" : pkgId;
    window.location.href = `/register?package=${encodeURIComponent(actual)}&currency=${encodeURIComponent(currency)}`;
  };
  const priceOf = (pkg) => {
    if (pkg.id === "delegate") return early ? RATES.delegate.early : RATES.delegate.standard;
    if (pkg.id === "standard") return RATES.delegate.standard;
    return RATES[pkg.id];
  };
  return (
    <section id="participate" data-testid="pricing-section" className="bg-[#f3f3e9] py-20 lg:py-28">
      <div className="wrap">
        <Reveal className="grid items-end gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <div className="eyebrow text-[#5c6b5a]">03 / Find your place</div>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl">
              Your next opportunity
              <br />
              <em className="italic">starts here.</em>
            </h2>
          </div>
          <div className="flex flex-col items-start gap-2 lg:items-end">
            <a href="/assets/ratecard.pdf" target="_blank" rel="noreferrer" data-testid="pricing-ratecard-link" className="text-link">
              View official rate card ↗
            </a>
            <a href="/assets/layout.pdf" target="_blank" rel="noreferrer" data-testid="pricing-layout-link" className="text-link">
              View expo floor plan ↗
            </a>
          </div>
        </Reveal>

        <Reveal className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Participation category">
            {TABS.map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                data-testid={`pricing-tab-${id}`}
                onClick={() => setTab(id)}
                className="rounded-full px-5 py-2.5 text-sm font-bold transition-colors duration-200"
                style={tab === id ? { background: "var(--forest)", color: "var(--lime)" } : { border: "1px solid var(--line)", color: "var(--forest)" }}
              >
                {label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm font-bold">
            Attendee category
            <select data-testid="pricing-currency" value={currency} onChange={(e) => setCurrency(e.target.value)} className="field-input !w-auto !py-2">
              <option value="INR">Indian · INR</option>
              <option value="USD">International · USD</option>
            </select>
          </label>
        </Reveal>

        <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3" data-testid="pricing-cards">
          {PACKAGES_BY_TAB[tab].map((pkg, i) => {
            const [inr, usd] = priceOf(pkg);
            const expired = pkg.id === "delegate" && !early;
            return (
              <Reveal key={pkg.id} delay={i * 0.07}>
                <article className="flex h-full flex-col rounded-xl p-7" style={{ background: "#fbfbf4", border: i === 0 ? "1.5px solid var(--forest)" : "1px solid var(--line)" }} data-testid={`price-card-${pkg.id}`}>
                  <span className="badge-lime self-start" style={i === 0 ? {} : { background: "#eaeadd" }}>
                    {tab === "delegate" ? (i === 0 ? (early ? "EARLY BIRD · UNTIL 10 NOVEMBER" : "STANDARD REGISTRATION") : "FROM 11 NOVEMBER 2026") : tab === "stall" ? "EXHIBITION PARTICIPATION" : "PARTICIPATION · PACKAGE 2"}
                  </span>
                  <h3 className="mt-4 font-display text-2xl">{expired ? pkg.later : pkg.name}</h3>
                  <p className="mt-2 flex-1 text-[13px] leading-relaxed text-[#3d5247]">{pkg.description}</p>
                  <div className="mt-5 font-display text-3xl sm:text-4xl" data-testid={`price-amount-${pkg.id}`}>
                    {money(currency === "INR" ? inr : usd, currency)} <small className="text-sm font-bold text-[#5c6b5a]">{currency === "INR" ? "+ GST" : ""}</small>
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#5c6b5a]">{currency === "INR" ? "GST will be itemised before payment." : "International attendee rate."}</p>
                  <button
                    onClick={() => goToRegister(pkg.id)}
                    disabled={pkg.disabledDuringEarly && early}
                    data-testid={`price-cta-${pkg.id}`}
                    className="ofic-btn ofic-btn-forest mt-5 justify-center"
                  >
                    {pkg.disabledDuringEarly && early ? "Available from 11 November" : "Register your interest"} <span className="arrow">↗</span>
                  </button>
                </article>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-6 text-[12px] text-[#5c6b5a]" data-testid="pricing-note">
          Early bird offer valid through 10 November 2026 (IST). Indian rates exclude GST. International rates are as published in the official rate card. Exhibition and awards participation are subject to organiser confirmation.
        </p>
      </div>
    </section>
  );
}

export function Future() {
  return (
    <section data-testid="future-section" className="bg-[#eaeadd] py-20 lg:py-28">
      <div className="wrap grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
        <Reveal>
          <figure className="overflow-hidden rounded-2xl">
            <img src="/assets/farm.jpg" alt="Green rows of organically grown vegetables" loading="lazy" className="h-80 w-full object-cover sm:h-[420px]" />
          </figure>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="eyebrow text-[#5c6b5a]">Grow the conversation</div>
          <h2 className="mt-4 font-display text-3xl sm:text-5xl">
            Better business.
            <br />
            <em className="italic">From the ground up.</em>
          </h2>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-[#3d5247]">
            Discover new supply and demand opportunities. Meet the people shaping organic food and beverage trade. Explore what comes next, together.
          </p>
          <a
            href={`mailto:${EVENT.email}?subject=OFIC%202026%20Partnership%20Enquiry`}
            data-testid="future-partner-cta"
            className="ofic-btn ofic-btn-forest mt-8"
          >
            Partner with OFIC <span className="arrow">↗</span>
          </a>
        </Reveal>
      </div>
    </section>
  );
}

export function Gallery() {
  const [open, setOpen] = useState(false);
  return (
    <section id="gallery" data-testid="gallery-section" className="bg-[#f3f3e9] py-20 lg:py-28">
      <div className="wrap">
        <Reveal className="grid items-end gap-6 lg:grid-cols-[1fr_1fr]">
          <div>
            <div className="eyebrow text-[#5c6b5a]">The OFIC journey / 2019</div>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl">
              Real connections.
              <br />
              <em className="italic">Memorable moments.</em>
            </h2>
          </div>
          <p className="text-[15px] font-bold text-[#5c6b5a] lg:text-right">
            A look back at OFIC 2019 — conversations,
            <br />
            shared ideas and recognition across the organic community.
          </p>
        </Reveal>
        <Reveal className="mt-10">
          <button onClick={() => setOpen(true)} data-testid="gallery-open" aria-label="Open OFIC 2019 photo gallery" className="group relative block w-full overflow-hidden rounded-2xl">
            <img src="/assets/ofic-2019-gallery.jpg" alt="OFIC 2019 event photo collage featuring speakers, panel discussions, the inaugural ceremony, award presentations and delegates" loading="lazy" className="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-[460px]" />
            <span className="absolute inset-0 grid place-items-center bg-[#0b2b21]/45 text-center text-[#f3f3e9] transition-colors group-hover:bg-[#0b2b21]/30">
              <span>
                <span className="eyebrow block text-[#d7ef8b]">OFIC 2019 · Event highlights</span>
                <strong className="mt-2 block font-display text-2xl">View full gallery ↗</strong>
              </span>
            </span>
          </button>
          <p className="mt-4 text-[12px] text-[#5c6b5a]">From the OFIC archives · Images supplied by the organiser.</p>
        </Reveal>
        {open && (
          <motion.div data-testid="gallery-lightbox" className="fixed inset-0 z-[70] grid place-items-center bg-[#0b2b21]/85 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={() => setOpen(false)}>
            <motion.div initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} className="max-w-4xl" onClick={(e) => e.stopPropagation()}>
              <div className="mb-3 flex items-center justify-between text-[#f3f3e9]">
                <h3 className="font-display text-xl">OFIC 2019 — event highlights</h3>
                <button data-testid="gallery-lightbox-close" aria-label="Close gallery" onClick={() => setOpen(false)} className="text-2xl">✕</button>
              </div>
              <img src="/assets/ofic-2019-gallery.jpg" alt="Full OFIC 2019 event collage" className="max-h-[75vh] w-full rounded-xl object-contain" />
              <p className="mt-3 text-center text-sm opacity-80">Speakers, panel discussions, inauguration, awards and the OFIC community.</p>
            </motion.div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

function downloadIcs() {
  const ics = "BEGIN:VCALENDAR\r\nVERSION:2.0\r\nBEGIN:VEVENT\r\nUID:ofic2026@teflas.com\r\nDTSTAMP:20261028T000000Z\r\nDTSTART;VALUE=DATE:20261211\r\nDTEND;VALUE=DATE:20261212\r\nSUMMARY:Organic Food India Conclave 2026\r\nLOCATION:Ginger Hotel\\, Mumbai\r\nDESCRIPTION:Conference - Exhibition - Business Networking & Awards. Contact events@teflas.com for exact timings and hotel location.\r\nEND:VEVENT\r\nEND:VCALENDAR";
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "OFIC-2026.ics";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function Venue() {
  return (
    <section id="venue" data-testid="venue-section" className="bg-[#eaeadd] py-20 lg:py-28">
      <div className="wrap">
        <Reveal className="grid items-end gap-6 lg:grid-cols-[1fr_1fr]">
          <div>
            <div className="eyebrow text-[#5c6b5a]">04 / Your next connection</div>
            <h2 className="mt-4 font-display text-3xl sm:text-5xl">
              Meet us in <em className="italic">Mumbai.</em>
            </h2>
          </div>
          <p className="text-[15px] font-bold text-[#5c6b5a] lg:text-right">
            One day to exchange ideas, build relationships
            <br />
            and explore the future of organic business.
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 lg:grid-cols-[1.5fr_1fr]">
          <Reveal>
            <div className="grid overflow-hidden rounded-2xl sm:grid-cols-2" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }} data-testid="venue-card">
              <figure className="relative min-h-[260px]">
                <img src="/assets/ginger-mumbai.jpg" alt="Ginger Hotel, Mumbai exterior" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                <figcaption className="absolute inset-x-0 bottom-0 p-3 text-[10px] font-extrabold tracking-[0.18em] text-[#f3f3e9]" style={{ background: "linear-gradient(to top, rgba(11,43,33,.85), transparent)" }}>
                  THE VENUE / GINGER HOTEL, MUMBAI
                </figcaption>
              </figure>
              <div className="p-7">
                <div className="flex items-start gap-4">
                  <div className="grid place-items-center rounded-lg px-4 py-3 text-center" style={{ background: "var(--forest)", color: "var(--lime)" }}>
                    <strong className="font-display text-2xl leading-none">11</strong>
                    <span className="mt-1 text-[9px] font-extrabold tracking-[0.14em]">DEC 2026</span>
                  </div>
                  <div>
                    <span className="eyebrow text-[10px] text-[#5c6b5a]">Friday · Save the date</span>
                    <h3 className="mt-1 font-display text-2xl">11 December, 2026</h3>
                    <button onClick={downloadIcs} data-testid="venue-add-to-calendar" className="text-link mt-2 !text-[13px]">
                      Add to calendar <span className="arrow">↗</span>
                    </button>
                  </div>
                </div>
                <div className="mt-7 border-t pt-6" style={{ borderColor: "var(--line)" }}>
                  <span className="eyebrow text-[10px] text-[#5c6b5a]">⌖ The meeting point</span>
                  <h3 className="mt-1 font-display text-2xl">Ginger Hotel, Mumbai</h3>
                  <p className="mt-2 text-[13px] leading-relaxed text-[#3d5247]">Contact the organiser for the exact hotel location and arrival information.</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <aside className="h-full rounded-2xl p-7" style={{ background: "var(--forest)", color: "var(--f3f3e9)" }}>
              <span className="eyebrow text-[#d7ef8b]">Let's talk organic</span>
              <h3 className="mt-3 font-display text-2xl leading-snug">
                Questions, partnerships
                <br />
                or group participation?
              </h3>
              <div className="mt-5 grid gap-2.5 text-sm font-bold">
                <a href={`mailto:${EVENT.email}`} className="no-underline" style={{ color: "var(--lime)" }}>{EVENT.email} ↗</a>
                {EVENT.phones.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="no-underline" style={{ color: "var(--lime)" }}>{p}</a>
                ))}
              </div>
              <img src="/assets/teflas-mark.png" alt="Organised by Tefla's" className="mt-6 h-8 w-auto" />
              <p className="mt-4 text-[12px] leading-relaxed opacity-80" style={{ whiteSpace: "pre-line" }}>{EVENT.address}</p>
            </aside>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const FAQS = [
  ["01", "Who should attend OFIC 2026?", "Farmers, cooperatives, seed producers, food companies, manufacturers, importers, exporters, buyers, retailers, supermarkets, researchers, consultants, investors, healthcare practitioners and policy makers across the organic ecosystem."],
  ["02", "What does the awards package cover?", "Package 2 covers only the award nomination entry fee and gala dinner on 11 December 2026, 7:00 pm onwards. Nomination does not guarantee an award. Contact the organiser for categories and nomination requirements."],
  ["03", "How do I register and pay?", "Choose your participation category and complete the registration form. Online checkout will open once payment configuration is activated. Until then, you can submit your registration interest and contact Tefla's for payment arrangements. A submitted interest form is not a confirmed booking."],
  ["04", "Where can I find payment and bank transfer details?", "The official rate card contains Indian NEFT and international bank transfer details. Please contact the organiser to confirm payment instructions and availability before transferring funds."],
];

export function Faq() {
  const brochure = useBrochureGate();
  return (
    <section id="faqs" data-testid="faq-section" className="bg-[#f3f3e9] py-20 lg:py-28">
      <div className="wrap grid gap-12 lg:grid-cols-[1fr_1.4fr]">
        <Reveal>
          <span className="eyebrow text-[#5c6b5a]">A little clarity, before you come</span>
          <h2 className="mt-4 font-display text-3xl sm:text-5xl">
            Good questions.
            <br />
            <em className="italic">Useful answers.</em>
          </h2>
          <p className="mt-5 text-[15px] font-bold text-[#5c6b5a]">
            Everything you need to take
            <br />
            your next step with OFIC 2026.
          </p>
          <a href={`mailto:${EVENT.email}`} data-testid="faq-help-link" className="mt-8 inline-flex items-center gap-3 rounded-xl p-4 no-underline" style={{ background: "#fbfbf4", border: "1px solid var(--line)" }}>
            <span className="grid h-9 w-9 place-items-center rounded-full" style={{ background: "var(--lime)" }}>↗</span>
            <span className="text-[13px] text-[#123d30]">
              Need a hand?
              <br />
              <strong>Talk to the OFIC team</strong>
            </span>
          </a>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="border-t" style={{ borderColor: "var(--line)" }}>
            {FAQS.map(([n, q, a]) => (
              <details key={n} className="faq-item group border-b" style={{ borderColor: "var(--line)" }} data-testid={`faq-item-${n}`}>
                <summary className="flex cursor-pointer list-none items-center gap-5 py-6">
                  <span className="font-display text-lg italic opacity-40">{n}</span>
                  <span className="flex-1 text-[15px] font-extrabold sm:text-lg">{q}</span>
                  <span className="faq-toggle text-2xl font-light transition-transform duration-300">+</span>
                </summary>
                <p className="max-w-3xl pb-6 pl-12 text-[14px] leading-relaxed text-[#3d5247]">{a}</p>
              </details>
            ))}
          </div>
          <button onClick={brochure} data-testid="faq-brochure-link" className="text-link mt-6 !text-[13px]">
            Download the brochure <span className="arrow">↓</span>
          </button>
        </Reveal>
      </div>
    </section>
  );
}

export { PACKAGE_LABELS };
